import { isResource } from '@medplum/core'
import type {
  AuditEvent,
  AuditEventAgent,
  AuditEventEntity,
  Bundle,
  ClientApplication,
  Device,
  Practitioner,
  Reference,
  Task,
} from '@medplum/fhirtypes'
import { compact, isEmpty } from 'lodash-es'
import { z } from 'zod'
import { codeableConcept } from '#src/codeableConcept.ts'
import {
  QUEUED_EDITS_AUDIT_EVENT_PROPOSED_CODE,
  QUEUED_EDITS_AUDIT_EVENT_TYPE_SYSTEM,
  QUEUED_EDITS_PROPOSAL_METHOD_SYSTEM,
} from '#src/codeSystems.ts'
import { ExtendedResource } from '#src/ExtendedResource.ts'
import {
  PROVENANCE_PARTICIPANT_TYPE_MAP,
  ProvenanceParticipantType,
} from '#src/models/provenance.ts'
import type { QueryTypes } from '#src/search.ts'
import { isOfType, now } from '#src/utils.ts'

// How the proposal was initiated, independent of the product, vendor, or
// transport that produced it. The open-ended application identity belongs in
// `site`; this stable taxonomy only drives audit semantics and presentation.
// Written to AuditEvent.subtype.
export const ProposalMethod = {
  // Produced without a human asking for this specific proposal.
  Automated: 'automated',
  // Produced by software/AI in response to a human request.
  Assisted: 'assisted',
  // Proposed directly by a human through an application.
  Direct: 'direct',
} as const
export type ProposalMethod =
  (typeof ProposalMethod)[keyof typeof ProposalMethod]

// Source-specific context is serialized into one AuditEvent entity detail.
// Keeping one stable detail key lets new products and vendors attach arbitrary
// JSON metadata without expanding the persisted schema.
const PROPOSAL_SOURCE_CONTEXT_DETAIL = 'source-context'

export type ProposalMetadataValue =
  | string
  | number
  | boolean
  | null
  | ProposalMetadataValue[]
  | { [key: string]: ProposalMetadataValue }

const proposalMetadataValueSchema: z.ZodType<ProposalMetadataValue> = z.lazy(
  () =>
    z.union([
      z.string(),
      z.number(),
      z.boolean(),
      z.null(),
      z.array(proposalMetadataValueSchema),
      z.record(proposalMetadataValueSchema),
    ])
)

export interface ProposalTrace {
  id: string
  // Open identifier for the tracing system, e.g. `seen` or `langfuse`.
  system?: string
}

const proposalTraceSchema = z.object({
  id: z.string(),
  system: z.string().optional(),
}) satisfies z.ZodType<ProposalTrace>

export interface ProposalDefinition {
  name: string
  input: Record<string, ProposalMetadataValue>
}

const proposalDefinitionSchema = z.object({
  name: z.string(),
  input: z.record(proposalMetadataValueSchema),
}) satisfies z.ZodType<ProposalDefinition>

const storedSourceContextJsonSchema = z.record(z.unknown())

// `AuditEvent.entity.name` values. The entity names, not their `type`/`role`
// codings, are what `get source()` / `get target()` key off, so they are part
// of the stored contract.
export const ProposalEntityName = {
  Proposal: 'proposal',
  ReviewTask: 'review-task',
  Trigger: 'trigger',
  Thread: 'thread',
} as const
export type ProposalEntityName =
  (typeof ProposalEntityName)[keyof typeof ProposalEntityName]

// Standard "software application" actor coding for the writing client, from the
// DICOM audit-message ontology AuditEvent is modeled on.
export const AUDIT_EVENT_AGENT_TYPE_DICOM_SYSTEM =
  'http://dicom.nema.org/resources/ontology/DCM'
export const AUDIT_EVENT_AGENT_TYPE_APPLICATION_CODE = '110150'

const applicationAgentType = codeableConcept({
  system: AUDIT_EVENT_AGENT_TYPE_DICOM_SYSTEM,
  code: AUDIT_EVENT_AGENT_TYPE_APPLICATION_CODE,
  display: 'Application',
})

export interface ProposalSource {
  method: ProposalMethod
  // Open-ended application/surface label, e.g. 'orbit-portal', 'seen-backend',
  // 'orbit-mcp', 'crm-slack-agent'.
  site: string
  // The software identity that wrote the proposal (the writing client's
  // profile).
  observer?: Reference<ClientApplication | Device | Practitioner>
  // The human on whose behalf the proposal was made, when known.
  requestor?: Reference<Practitioner>
  // Display name for a non-FHIR human actor (Slack user, CLI user) when no
  // Practitioner reference exists.
  requestorName?: string
  // The resource/event that triggered an event-driven proposal, ideally
  // version-pinned (`Patient/x/_history/3`).
  trigger?: Reference
  // The conversation the proposal came out of (AIThread Communication, etc).
  thread?: Reference
  // Generic trace correlation shown to technical reviewers.
  trace?: ProposalTrace
  // Link back to the originating interaction, when one exists.
  sourceUrl?: string
  // Stable queued-edit definition and the JSON input needed to rerun it.
  definition?: ProposalDefinition
  // Product/vendor-specific JSON. Nested objects and arrays are supported and
  // require no changes to this schema.
  metadata?: Record<string, ProposalMetadataValue>
  // Free-text one-liner, e.g. 'Disenrollment cleanup after participant left the
  // program'.
  description?: string
}

export interface ProposalSourceTarget {
  bundle: Reference<Bundle>
  task?: Reference<Task>
}

function entityByName(
  resource: AuditEvent,
  name: ProposalEntityName
): AuditEventEntity | undefined {
  return resource.entity?.find((entity) => entity.name === name)
}

export class QueuedEditsProposalAuditEvent extends ExtendedResource<AuditEvent> {
  static readonly RESOURCE_TYPE = 'AuditEvent'

  static isInstance(value: unknown): value is AuditEvent {
    return (
      isResource(value) &&
      isOfType(value, 'AuditEvent') &&
      !!value.type &&
      value.type.system === QUEUED_EDITS_AUDIT_EVENT_TYPE_SYSTEM &&
      value.type.code === QUEUED_EDITS_AUDIT_EVENT_PROPOSED_CODE
    )
  }

  static build(
    target: ProposalSourceTarget,
    source: ProposalSource
  ): QueuedEditsProposalAuditEvent {
    const { bundle, task } = target
    const {
      method,
      site,
      observer,
      requestor,
      requestorName,
      trigger,
      thread,
      trace,
      sourceUrl,
      definition,
      metadata,
      description,
    } = source

    const requestorAgent: AuditEventAgent | undefined =
      requestor || requestorName
        ? {
            type: PROVENANCE_PARTICIPANT_TYPE_MAP[
              ProvenanceParticipantType.AUTHOR
            ],
            ...(requestor ? { who: requestor } : { name: requestorName }),
            requestor: true,
          }
        : undefined

    const observerAgent: AuditEventAgent | undefined = observer
      ? { type: applicationAgentType, who: observer, requestor: false }
      : undefined

    // AuditEvent requires at least one agent; fall back to naming the surface.
    const agent: AuditEventAgent[] = compact([requestorAgent, observerAgent])
    if (isEmpty(agent)) {
      agent.push({ requestor: false, name: site })
    }

    const sourceContext: StoredSourceContext = {}
    if (trace?.id) {
      sourceContext.trace = trace
    }
    if (sourceUrl) {
      sourceContext.sourceUrl = sourceUrl
    }
    if (definition) {
      sourceContext.definition = definition
    }
    if (metadata && !isEmpty(metadata)) {
      sourceContext.metadata = metadata
    }
    const detail = isEmpty(sourceContext)
      ? []
      : [
          {
            type: PROPOSAL_SOURCE_CONTEXT_DETAIL,
            valueString: JSON.stringify(sourceContext),
          },
        ]

    const proposalEntity: AuditEventEntity = {
      what: bundle,
      name: ProposalEntityName.Proposal,
    }
    if (!isEmpty(detail)) {
      proposalEntity.detail = detail
    }
    const entity: AuditEventEntity[] = compact([
      proposalEntity,
      task ? { what: task, name: ProposalEntityName.ReviewTask } : undefined,
      trigger ? { what: trigger, name: ProposalEntityName.Trigger } : undefined,
      thread ? { what: thread, name: ProposalEntityName.Thread } : undefined,
    ])

    const auditEvent: Omit<AuditEvent, 'resourceType'> = {
      type: {
        system: QUEUED_EDITS_AUDIT_EVENT_TYPE_SYSTEM,
        code: QUEUED_EDITS_AUDIT_EVENT_PROPOSED_CODE,
        display: 'Queued edits proposed',
      },
      subtype: [{ system: QUEUED_EDITS_PROPOSAL_METHOD_SYSTEM, code: method }],
      action: 'C',
      outcome: '0',
      recorded: now(),
      agent,
      // AuditEvent.source.observer is required in R4B; a display-only Reference
      // is legal when no software identity was supplied.
      source: { site, observer: observer ?? requestor ?? { display: site } },
      entity,
    }
    if (description) {
      auditEvent.outcomeDesc = description
    }
    return new QueuedEditsProposalAuditEvent(auditEvent)
  }

  static byProposal(bundle: Reference<Bundle>): QueryTypes {
    return {
      entity: bundle,
      type: `${QUEUED_EDITS_AUDIT_EVENT_TYPE_SYSTEM}|${QUEUED_EDITS_AUDIT_EVENT_PROPOSED_CODE}`,
      _sort: '-date',
      _count: '1000',
    }
  }

  get source(): ProposalSource | undefined {
    const { resource } = this
    if (!QueuedEditsProposalAuditEvent.isInstance(resource)) {
      return undefined
    }

    const method = resource.subtype?.find(
      (coding) => coding.system === QUEUED_EDITS_PROPOSAL_METHOD_SYSTEM
    )?.code as ProposalMethod | undefined
    const site = resource.source?.site
    if (!method || !site) {
      return undefined
    }

    const requestorAgent = resource.agent?.find((a) => a.requestor)
    const observerAgent = resource.agent?.find((a) => !a.requestor && !!a.who)

    const proposal = entityByName(resource, ProposalEntityName.Proposal)
    const trigger = entityByName(resource, ProposalEntityName.Trigger)?.what
    const thread = entityByName(resource, ProposalEntityName.Thread)?.what
    const context = parseSourceContext(
      proposal?.detail?.find(
        (detail) => detail.type === PROPOSAL_SOURCE_CONTEXT_DETAIL
      )?.valueString
    )

    const proposalSource: ProposalSource = { method, site }
    if (observerAgent?.who) {
      proposalSource.observer = observerAgent.who as Reference<
        ClientApplication | Device | Practitioner
      >
    }
    if (requestorAgent?.who) {
      proposalSource.requestor = requestorAgent.who as Reference<Practitioner>
    }
    if (!requestorAgent?.who && requestorAgent?.name) {
      proposalSource.requestorName = requestorAgent.name
    }
    if (trigger) {
      proposalSource.trigger = trigger as Reference
    }
    if (thread) {
      proposalSource.thread = thread as Reference
    }
    if (context?.trace) {
      proposalSource.trace = context.trace
    }
    if (context?.sourceUrl) {
      proposalSource.sourceUrl = context.sourceUrl
    }
    if (context?.definition) {
      proposalSource.definition = context.definition
    }
    if (context?.metadata) {
      proposalSource.metadata = context.metadata
    }
    if (resource.outcomeDesc) {
      proposalSource.description = resource.outcomeDesc
    }
    return proposalSource
  }

  get target(): ProposalSourceTarget | undefined {
    const { resource } = this
    const bundle = entityByName(resource, ProposalEntityName.Proposal)?.what
    if (!bundle) {
      return undefined
    }
    const task = entityByName(resource, ProposalEntityName.ReviewTask)?.what
    const target: ProposalSourceTarget = {
      bundle: bundle as Reference<Bundle>,
    }
    if (task) {
      target.task = task as Reference<Task>
    }
    return target
  }

  get recorded(): string | undefined {
    return this.resource.recorded
  }
}

interface StoredSourceContext {
  trace?: ProposalTrace
  sourceUrl?: string
  definition?: ProposalDefinition
  metadata?: Record<string, ProposalMetadataValue>
}

function parseSourceContext(value: string | undefined): StoredSourceContext {
  if (!value) {
    return {}
  }
  try {
    const parsed = storedSourceContextJsonSchema.safeParse(JSON.parse(value))
    if (!parsed.success) {
      return {}
    }
    const { trace, sourceUrl, definition, metadata } = parsed.data
    const parsedTrace = proposalTraceSchema.safeParse(trace)
    const parsedSourceUrl = z.string().safeParse(sourceUrl)
    const parsedDefinition = proposalDefinitionSchema.safeParse(definition)
    const parsedMetadata = z
      .record(proposalMetadataValueSchema)
      .safeParse(metadata)
    const context: StoredSourceContext = {}
    if (parsedTrace.success) {
      context.trace = parsedTrace.data
    }
    if (parsedSourceUrl.success) {
      context.sourceUrl = parsedSourceUrl.data
    }
    if (parsedDefinition.success) {
      context.definition = parsedDefinition.data
    }
    if (parsedMetadata.success) {
      context.metadata = parsedMetadata.data
    }
    return context
  } catch {
    return {}
  }
}
